import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { ArrowLeft, LogIn, LogOut, X, Receipt, Send } from "lucide-react";
import { toast } from "sonner";

export default function ReservationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [lot, setLot] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/reservations/${id}`);
      setR(data);
      const [t, l] = await Promise.all([
        api.get(`/tenants/${data.tenant_id}`),
        api.get(`/lots/${data.lot_id}`),
      ]);
      setTenant(t.data);
      setLot(l.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [id]);

  const action = async (path, label) => {
    try {
      await api.post(`/reservations/${id}/${path}`);
      toast.success(label);
      load();
    } catch (e) {
      // Show server's detail message (e.g. "Lot is currently occupied by Jane Doe …")
      // so the operator knows exactly what's blocking the check-in.
      // 409 = operational conflict (not a system error), so show as amber warning.
      const detail = e?.response?.data?.detail || "Action failed";
      if (e?.response?.status === 409) {
        toast.warning(detail, { duration: 8000 });
      } else {
        toast.error(detail, { duration: 8000 });
      }
    }
  };

  const createInvoice = async () => {
    try {
      const { data } = await api.post(`/invoices`, {
        tenant_id: r.tenant_id,
        reservation_id: r.id,
        description: `Reservation ${r.confirmation_no}`,
        items: [{ description: "Lot rent", amount: r.sub_total }],
        tax_rate: r.sub_total ? r.rent_tax / r.sub_total : 0,
      });
      if (data?._utilities_applied?.length) {
        toast.success(`Invoice created — utilities added: ${data._utilities_applied.join(", ")}`, { duration: 6000 });
      } else {
        toast.success("Invoice created");
      }
      navigate("/invoices");
    } catch { toast.error("Failed to create invoice"); }
  };

  if (!r) return <div className="p-8 text-sm text-[#737A76]">Loading…</div>;

  return (
    <div data-testid="reservation-detail-page">
      <Button variant="ghost" onClick={() => navigate("/reservations")} className="-ml-2 mb-2"><ArrowLeft size={14} className="mr-1" /> Reservations</Button>
      <PageHeader
        title={`Reservation ${r.confirmation_no}`}
        subtitle={<span className="flex items-center gap-2">{r.lease_type} <StatusBadge status={r.status} /></span>}
        actions={
          <div className="flex items-center gap-2">
            {r.status === "Reservation" && (
              <Button onClick={() => action("check-in", "Checked in")} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="check-in-button">
                <LogIn size={14} className="mr-2" /> Check In
              </Button>
            )}
            {r.status === "Active" && (
              <Button onClick={() => action("check-out", "Checked out")} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="check-out-button">
                <LogOut size={14} className="mr-2" /> Check Out
              </Button>
            )}
            {r.status !== "Cancelled" && r.status !== "Closed" && (
              <Button variant="outline" className="border-[#A13E34] text-[#A13E34] hover:bg-[#F5E3E0]" onClick={() => action("cancel", "Reservation cancelled")} data-testid="cancel-reservation-button">
                <X size={14} className="mr-2" /> Cancel
              </Button>
            )}
            <Button variant="outline" onClick={createInvoice} className="border-[#EAEAE5]" data-testid="create-invoice-button">
              <Receipt size={14} className="mr-2" /> Generate Invoice
            </Button>
            <Button variant="outline" className="border-[#EAEAE5]" onClick={async () => {
              try {
                const { data } = await api.post(`/integrations/ghl/reservations/${id}/sync`);
                toast.success(`Synced to FreedomCRM (opportunity ${data.ghl_opportunity_id || "—"})`);
                load();
              } catch (e) { toast.error(e.response?.data?.detail || "CRM sync failed"); }
            }} data-testid="push-reservation-ghl-button">
              <Send size={14} className="mr-2" /> Push to CRM
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#EAEAE5] shadow-none lg:col-span-2"><CardContent className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <Info label="Tenant" value={tenant ? `${tenant.first_name} ${tenant.last_name}` : "—"} onClick={tenant ? () => navigate(`/tenants/${tenant.id}`) : null} />
            <Info label="Lot" value={lot ? `#${lot.number} (${lot.size})` : "—"} />
            <Info label="Check in" value={formatDate(r.check_in)} />
            <Info label="Check out" value={formatDate(r.check_out)} />
            <Info label="Days" value={r.days} />
            <Info label="Daily rate" value={formatCurrency(r.daily_rate)} />
            <Info label="Source" value={r.source} />
            <Info label="Deposit" value={formatCurrency(r.deposit)} />
          </div>
        </CardContent></Card>

        <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-2.5">
          <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-2">Charges</div>
          <Row label="Rent" value={formatCurrency(r.rent_amount)} />
          <Row label="POPM fee" value={formatCurrency(r.popm_fee)} />
          <Row label="Discount" value={`- ${formatCurrency(r.discount)}`} />
          <Row label="Subtotal" value={formatCurrency(r.sub_total)} bold />
          <Row label="Tax" value={formatCurrency(r.rent_tax)} />
          <div className="border-t border-[#EAEAE5] my-2" />
          <Row label="Total" value={formatCurrency(r.total)} bold large />
        </CardContent></Card>
      </div>
    </div>
  );
}

function Info({ label, value, onClick }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">{label}</div>
      <div className={`text-sm font-medium ${onClick ? "text-[#284B3B] cursor-pointer hover:underline" : ""}`} onClick={onClick}>{value}</div>
    </div>
  );
}
function Row({ label, value, bold, large }) {
  return (
    <div className={`flex justify-between ${large ? "text-base" : "text-sm"}`}>
      <span className="text-[#737A76]">{label}</span>
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}
